/**
 * Contact information does not belong in a name field.
 *
 * A user registered as `Mhmodmosy@gmail.com gmail.com`. Profiles are anonymous
 * in browse, but a matched partner sees the other person's name in the
 * conversation — so an email in the name box hands out contact details one-way
 * and bypasses the mutual phone-exchange gate that exists so both sides consent.
 *
 * This is the CLIENT half, and it is the half that helps: it tells the person
 * what is wrong so they can fix their own name. The server separately strips
 * contact info on write (`backend/utils/person_name.py`), because old installed
 * apps do not know about this rule and must still be able to register (RULE #1).
 * That backstop is why the pattern lists drifting apart costs at most a missing
 * warning, never a leak.
 *
 * The rule is deliberately narrow. Tinder bans contact info in public profile
 * content and strips handles from bios; Bumble forbids messaging-platform details
 * in usernames; LinkedIn forbids email addresses in the name fields. But
 * Facebook's broader policy — no numbers, no punctuation, no mixed scripts — is
 * the most criticised name policy there is, because it flags legitimate
 * non-Western names as fake. W3C's personal-names guidance is explicit: allow
 * non-Latin scripts, hyphens, apostrophes and spaces, and don't assume a
 * one-letter name is an initial. So this checks for contact information and
 * judges nothing else.
 */

// Keep in step with PERSON_NAME_BAD_PATTERNS in backend/utils/person_name.py.
const BAD_PATTERNS = [
  // 6+ consecutive digits: an Egyptian mobile is 11, landlines 8+. The threshold
  // stays at 6 so a year-suffix nickname like "أبو محمد 1995" still passes.
  /\d{6,}/,
  // Arabic-numeral mobile prefixes ٠١٠ / ٠١١ / ٠١٢ / ٠١٥
  /٠١[٠١٢٥]/,
  // @handle
  /@[A-Za-z0-9_]+/,
  // URLs and messaging-app domains
  /(https?:\/\/|www\.|\.com|\.net|t\.me)/i,
  // App-routing keywords. WhatsApp's Arabic spellings were missing from the
  // original server list even though every OTP we send goes over WhatsApp.
  /wha?ts?app|telegram|instagram|snapchat|واتساب|واتس ?اب|وتساب|تيليجرام|تليجرام|انستجرام|انستغرام|انستقرام|سناب/i,
];

const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;

/**
 * True when the name contains an email, handle, URL, long digit run or a
 * messaging-app keyword.
 * @param {string|null|undefined} raw
 * @returns {boolean}
 */
export const nameHasContactInfo = (raw) => {
  if (typeof raw !== 'string' || !raw.trim()) return false;
  if (EMAIL.test(raw)) return true;
  return BAD_PATTERNS.some((pattern) => pattern.test(raw));
};

/** The server's limit (`_validate_display_name`). */
export const MAX_DISPLAY_NAME_LENGTH = 30;

/**
 * The first word of the name a member registered with, offered as "the name
 * others will see" — a suggestion in an editable field, as Hinge and Muzz
 * pre-fill from sign-up. Nothing that looks like contact information or
 * carries a digit is offered (a Google "name" is sometimes an email).
 * Mirrors `PersonName.suggestedDisplayName` on mobile.
 * @param {string|null|undefined} registrationName
 * @returns {string}
 */
export const suggestedDisplayName = (registrationName) => {
  const first = (typeof registrationName === 'string' ? registrationName.trim() : '').split(/\s+/)[0] || '';
  if (!first || nameHasContactInfo(first) || /[0-9٠-٩]/.test(first)) return '';
  return first.length > MAX_DISPLAY_NAME_LENGTH ? '' : first;
};

/**
 * The i18n key of the error for a display name, or null when it may be saved.
 * Required on a new profile (owner decision, 2026-10-20).
 * @param {string|null|undefined} value
 * @returns {string|null}
 */
export const displayNameErrorKey = (value) => {
  const name = typeof value === 'string' ? value.trim() : '';
  if (!name) return 'form.validationDisplayName';
  if (nameHasContactInfo(name)) return 'form.validationDisplayNameContact';
  return null;
};

export default nameHasContactInfo;
